/* jshint esversion: 6 */
import React, { useState, useEffect, useRef } from 'react';
import Utilities from 'app/utility/Utilities.js';
import { useCurrentBreakpointName } from 'react-socks';
import CalendarToolbar from 'app/components/Queue/CalendarComponents/CalendarToolbar.jsx';
import CalendarCell from 'app/components/Queue/CalendarComponents/CalendarCell.jsx';

const DlvrCalendar = (props) => {
    const [ currentView, setCurrentView ] = useState('month');
    const [ events, setEvents ] = useState({});
    const [ today, setToday ] = useState(new moment());
    const [ displayDate, setDisplayDate ] = useState(new moment());
    const [ timezone, setTimezone ] = useState('America/Los_Angeles');
    const [ weekdayCount, setWeekdayCount ] = useState(7);
    const [ activeCell, setActiveCell ] = useState([null, null]);
    const KEY_FORMAT = 'YYYY-MM-DD';
    const tableRef = useRef();
    const currentBreakpoint = useCurrentBreakpointName();

    useEffect(() => {
        let nextEvents = {};

        for (let i = 0; i < props.events.length; ++i) {
            let event = props.events[i];
            if (event.queuetime) {
                let key = new moment(event.queuetime).tz(timezone).format(KEY_FORMAT);
                if (!nextEvents[key]) {
                    nextEvents[key] = [];
                }
                nextEvents[key].push(Utilities.deepClone(event));
            }
        }
        setEvents(nextEvents);
    }, [props.events, props.timezone]);

    useEffect(() => {
        if (currentView !== 'week' && props.currentView === 'week') {
            if (today.month() === displayDate.month()) {
                setDisplayDate(new moment(today));
            } else {
                setDisplayDate(new moment(displayDate.date(1)));
            }
        }
        setCurrentView(props.currentView);
        setActiveCell([null, null]);
    }, [props.currentView]);

    useEffect(() => {
        if (props.timezone) {
            setTimezone(props.timezone);
        }
        setToday(new moment().tz(timezone));
        dateGenerator(currentView);
    }, [props.timezone]);


    useEffect(() => {
        if (currentView === 'week' && tableRef.current) {
           let offset = 800;
           tableRef.current.scrollTo(0, offset);
        }
    }, [currentView]);

    const dateGenerator = (period) => {
        let startDate = null,
            endDate = null,
            dates = [],
            incrementer = 0;

        if (period === 'month') {
            let firstOfMonth = new moment(displayDate).date(1),
                priorMonthStart = firstOfMonth.day(),
                endOfMonth = new moment(displayDate).date(displayDate.daysInMonth()),
                nextMonthEnd = endOfMonth.day();

            startDate = firstOfMonth.subtract(priorMonthStart, 'days');
            endDate = endOfMonth.add((6 - nextMonthEnd), 'days');

        } else if (period === 'week') {
            startDate = new moment(displayDate).subtract(displayDate.day(), 'days');
            startDate.hour(0);
            startDate.minute(0);
            startDate.seconds(0);
            // add one less day than you'd think since
            // setting startDate to midnight will tick endDate over to the next day
            endDate = new moment(startDate).add(5, 'days');
        }

        while (startDate <= endDate) {
            let key = startDate.add(incrementer, 'days').format(KEY_FORMAT);
            dates.push(key);
            if (!incrementer) {
                incrementer = 1;
            }
        }

        return dates;

    };

    const navigate = (direction) => {
        let unit = currentView === 'month' ? 'month' : 'week',
            nextDisplayDate = null;

        if (direction === 'PREV') {
            nextDisplayDate = new moment(displayDate).subtract(1, unit);
        } else if (direction === 'NEXT') {
            nextDisplayDate = new moment(displayDate).add(1, unit);
        }

        setDisplayDate(nextDisplayDate);
        props.navigateCallback(nextDisplayDate);
    };

    const calendarGenerator = () => {
        let dates = dateGenerator(currentView),
            tableHeader = null,
            tableContent = [],
            headerFormat = 'dddd',
            headerLabels = [],
            gridDefinition = [Math.round(dates.length/7), 7],
            incrementer = 1;

        const getCellEvents = (date, hour) => {
            let cellEvents = [];

            if (events[date] !== undefined) {
                if (hour !== undefined) {
                    events[date].map((event) => {
                        let eventTime = new moment(event.queuetime).tz(timezone);
                        
                        if (eventTime.hour() === hour) {
                            cellEvents.push(event);
                        } 
                    });
                } else {
                    return events[date];
                }
            }

            return cellEvents;
        }

        let dateKeys = dates.slice(0, 7);
        if (currentView === 'week') {
            headerFormat = 'DD dddd';
            if (currentBreakpoint === 'mobile') {
                headerFormat = 'DD ddd'
            }
            gridDefinition = [24, weekdayCount];
            dateKeys = [];
            dates.map((key) => {
                let dateKey = new moment(key).format(KEY_FORMAT);
                if (dateKeys.indexOf(dateKey) === -1) {
                    dateKeys.push(dateKey);
                }
            });
        } else if (currentBreakpoint === 'mobile') {
            headerFormat = 'ddd';
        }

        for (let i = 0; i < dateKeys.length; ++i) {
            headerLabels.push(new moment(dateKeys[i]).format(headerFormat));
        };

        tableHeader = (
            <thead className={`dlvr-calendar-header ${currentView}`}>
                <tr>
                    {headerLabels.map((label, i) => <th key={`header-${i}`} className={`calendar-header-cell ${currentView}`}>{label}</th>)}
                </tr>
            </thead>
        );

        let [rows, columns] = gridDefinition;

        let dayIndex = 0,
            tableRows = [];

        for (let i = 0; i < rows; ++i) {
            let tableCells = [];

            for (let j = 0; j < columns; ++j) {
                let tableCellClass = 'calendar-data-cell',
                    cellDate = new moment(dates[dayIndex]),
                    cellEvents = [];

                if (currentView === 'week') {
                    cellDate = new moment(dateKeys[j]);
                    cellDate.hour(i);
                    cellEvents = getCellEvents(cellDate.format(KEY_FORMAT), i);
                } else {
                    cellEvents = getCellEvents(cellDate.format(KEY_FORMAT));
                }

                if (currentView === 'month' && cellDate.month() !== displayDate.month()) {
                    tableCellClass += ' not-current';
                } else if (cellDate === today) {
                    tableCellClass += ' today';
                }

                if (currentView === 'month') {
                    tableCells.push(
                        <td key={`row-${i}_cell-${j}`} className={tableCellClass}>
                            <CalendarCell
                                {...props}
                                timezone={timezone}
                                currentView={currentView}
                                cellEvents={cellEvents}
                                cellDate={cellDate}
                                today={today}
                                showCellHeader={true}
                                cellHour={null}
                                row={i}
                                column={j}
                                activeCell={activeCell}
                                setActiveCell={setActiveCell}/>
                        </td>
                    );
                } else if (currentView === 'week') {
                    let showCellHeader = false;
                    if (j === 0 && i % 2 === 0) {
                        showCellHeader = true
                    }
                    tableCells.push(
                        <td key={`row-${i}_cell-${j}`} className={tableCellClass}>
                            <CalendarCell
                                {...props}
                                currentView={currentView}
                                timezone={timezone}
                                cellEvents={cellEvents}
                                cellDate={cellDate}
                                today={today}
                                showCellHeader={showCellHeader}
                                cellHour={i}
                                row={i}
                                column={j}
                                activeCell={activeCell}
                                setActiveCell={setActiveCell}/>
                        </td>
                    );
                }
                dayIndex++;
            }

            tableRows.push(<tr key={`row-${i}`}>{tableCells}</tr>);
        }

        tableContent = (
            <tbody className={`dlvr-calendar-body ${currentView}`}>
                {tableRows}
            </tbody>
        );

        return (
            <div className={`dlvr-calendar-table-wrapper ${currentView}`} ref={tableRef}>
                <table className={`dlvr-calendar-table ${currentView}`}>
                    {tableHeader}
                    {tableContent}
                </table>
            </div>
        );
    };



    return (
        <div className="dlvr-calendar-wrapper">
            <div className="dlvr-calendar-controls">
                <CalendarToolbar
                    displayDate={displayDate}
                    currentView={currentView}
                    timezone={timezone}
                    navigate={navigate}
                    {...props}/>
            </div>
            {calendarGenerator()}
        </div>
    );
};

DlvrCalendar.displayName = "DlvrCalendar";

export default DlvrCalendar;
